import { useEffect, useMemo, useState } from 'react'
import TemplatePicker from '../../../templates/TemplatePicker'
import { ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { useHistory } from '../../../../providers/HistoryProvider'
import { TemplateType } from '@neum/frontend-shared/constants/templateData'
import { isMobileDevice } from '../../../../utils/deviceUtils'
import { WarningBanner } from '../../../common/WarningBanner'
import { Spinner } from '../../../../utils/Spinner'
import VetRecButton from '../../../common/VetRecButton'

interface RegenerateNotesMainProps {
    onReselectRecordings: () => void;
    handleGenerate: (additionalInstructions?: string) => Promise<void>;
    recordingsToUse: string[] | undefined;
}

export default function RegenerateNotesMain({ onReselectRecordings, handleGenerate, recordingsToUse }: RegenerateNotesMainProps) {
    const {
        notesReprocessVisible, 
        handleNotesReprocessVisible, 
        template, 
        setTemplate, 
        selectedNoteMetadata, 
        activeSession, 
        didUserSaveNotesAfterLatestGeneration
    } = useHistory()
    
    const isMobile = useMemo(isMobileDevice, [])
    const [additionalInstructions, setAdditionalInstructions] = useState<string>()

    async function handleReprocessClicked(additionalInstructions?: string) {
        await handleGenerate(additionalInstructions)
        handleNotesReprocessVisible(false)
    }

    useEffect(() => {
        if (!notesReprocessVisible) {
            setAdditionalInstructions(undefined)
            setTemplate(undefined)    
        }
    }, [notesReprocessVisible, setTemplate])
    
    return (
        <div className="mt-5 sm:mt-6 flex flex-col gap-y-6">
            <div>
                <TemplatePicker template={template} templateIdForce={template ? `${template.organization}/${template.id}` : selectedNoteMetadata?.template_id} setTemplate={setTemplate} className={""} pickerWidth={'w-full'} templateBackground={"bg-white"} type={TemplateType.MEDICAL} pickerDirection={isMobile ? "above" : "below"} externalTemplateId={activeSession?.external_template_id}/>
            </div>
            <div className='relative'>
                <label
                    htmlFor="name"
                    className={`absolute -top-2 left-2 px-1 text-xs font-medium text-main-text-darker z-[0] bg-white flex gap-x-2`}
                >
                    Additional Instructions
                </label>
                <textarea
                    className='w-full border border-gray-300 rounded-md p-4 text-sm text-gray-600 z-[-1] -mb-1'
                    value={additionalInstructions}
                    onChange={(event) => setAdditionalInstructions(event.target.value)}
                    placeholder={"[Optional] Let VetRec know any additional instructions for the notes."}
                />
            </div>
            <button 
                className={`flex items-center justify-between text-blue-600 hover:text-blue-800 p-3 rounded-md border border-gray-300 cursor-pointer ${
                    recordingsToUse 
                        ? "bg-gradient-to-r from-[#cce0ff] to-[#d9fff6]" 
                        : "bg-gray-50 hover:bg-gradient-to-r from-[#cce0ff] to-[#d9fff6]"
                }`}
                onClick={onReselectRecordings}
            >
                <div className="flex items-center gap-x-2">
                    <span className="text-sm text-gray-700">
                        {recordingsToUse
                            ? <>Re-generate using <span className="font-bold text-blue-600">{recordingsToUse.length.toString()} selected recording{recordingsToUse.length > 1 ? "s" : ""}</span></>
                            : "Re-generate using the same recordings"}
                    </span>
                </div>
                <span
                    className="text-sm font-medium flex items-center gap-x-1"
                    
                >
                    Change
                    <ArrowRightIcon className="h-3 w-3" />
                </span>
            </button>
            <VetRecButton
                type="button"
                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 h-10 gap-x-2"
                onClick={() => handleReprocessClicked(additionalInstructions)}
                disabled={!template}
                showProcessing
                processingText="Regenerating note..."
            >
                <SparklesIcon className='h-4 w-4' />
                {template ? "Regenerate note" : <Spinner size="h-5 w-5 text-white" timer={false}/>}
            </VetRecButton>
            {didUserSaveNotesAfterLatestGeneration && <WarningBanner warningText="Regenerating notes will override any manual edits you made." className=""/>}
        </div>
    )
}